import type { Project } from '../../lib/projects';

/**
 * A project's cover image, or something to look at when it has none.
 *
 * Most projects have no cover, so the three lists that show one were showing
 * columns of identical empty grey squares -- which read as images that failed
 * to load rather than as images that were never set. An initial on a tinted
 * tile is at least the project's own, and gives the eye something to track
 * down a long list by.
 *
 * The tint is picked from the name rather than stored, so it is stable for a
 * given project without needing a column for it, and the palette is the muted
 * end of the same hues the status badges use -- this sits next to one.
 */
const TILE_TINTS = [
  'bg-stone-100 text-stone-500',
  'bg-honey-100 text-honey-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];

/**
 * The same seven hues, two steps paler.
 *
 * A card's cover is a wide banner rather than a 40px tile, so it carries many
 * times the area -- and the tile palette across a full grid of them
 * turns the page into a colour chart, which is the thing the status badges are
 * carefully avoiding two lines below. Same hue per project, much less of it.
 */
const BANNER_TINTS = [
  'bg-stone-100 text-stone-300',
  'bg-honey-50 text-honey-300',
  'bg-sky-50 text-sky-200',
  'bg-violet-50 text-violet-200',
  'bg-emerald-50 text-emerald-200',
  'bg-rose-50 text-rose-200',
  'bg-teal-50 text-teal-200',
];

/**
 * A cheap stable hash. Not security-sensitive; it only has to return the same
 * number every time for the same name.
 *
 * Exported because ProjectCard's cover is the same idea at a different size --
 * a 16:9 banner rather than a tile -- and the two must land on the same colour
 * for a given project or the list and the grid disagree about what it looks
 * like.
 */
export function tintFor(name: string, variant: 'tile' | 'banner' = 'tile'): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  const palette = variant === 'banner' ? BANNER_TINTS : TILE_TINTS;
  return palette[Math.abs(hash) % palette.length]!;
}

export function ProjectThumb({
  project,
  /** Sized by the caller: the dashboard column runs smaller than the list. */
  className = 'h-10 w-10',
}: {
  project: Project;
  className?: string;
}) {
  if (project.imageUrl) {
    return (
      <img
        src={project.imageUrl}
        alt=""
        loading="lazy"
        className={`shrink-0 rounded-lg object-cover ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-lg text-sm font-semibold uppercase ${tintFor(
        project.name,
      )} ${className}`}
    >
      {project.name.trim()[0] ?? '?'}
    </span>
  );
}
